<template>
  <InfoTooltip
    :title="t('TOOLTIPS.whalePoints.title')"
    :text="t('TOOLTIPS.whalePoints.text')"
    position="bottom"
    :disabled="disableTooltip"
  >
    <div ref="pillRef" class="wp-pill">
      <WpIcon fontSize="12px" />
      {{ formatWP(displayValue) }}
    </div>
  </InfoTooltip>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { gsap } from 'gsap'
import WpIcon from '@/components/WpIcon.vue'
import InfoTooltip from '@/components/InfoTooltip.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    whalePoints: number
    fixed?: boolean
    disableTooltip?: boolean
  }>(),
  {
    fixed: false,
    disableTooltip: false,
  },
)

const pillRef = ref<HTMLElement | null>(null)
const displayValue = ref(props.whalePoints)

function formatWP(wp: number): string {
  if (wp >= 1000000) return `${(wp / 1000000).toFixed(1)}M`
  if (wp >= 1000) return `${(wp / 1000).toFixed(1)}K`
  return Math.floor(wp).toString()
}

function animatePill(newValue: number) {
  if (!pillRef.value) return

  const oldValue = displayValue.value
  const tl = gsap.timeline()

  // Glow effect
  tl.to(pillRef.value, {
    boxShadow: '0 0 12px rgba(185, 234, 69, 0.4), 0 0 24px rgba(185, 234, 69, 0.2)',
    duration: 0.3,
    ease: 'power2.out',
  })

  // Animate the number incrementally
  tl.to(displayValue, {
    value: newValue,
    duration: 0.5,
    ease: 'power2.out',
    onUpdate: () => {
      displayValue.value = Math.floor(displayValue.value)
    },
    onComplete: () => {
      displayValue.value = newValue
    }
  }, 0.1)

  // Fade out glow (happens while number is still animating)
  tl.to(pillRef.value, {
    boxShadow: '0 0 0px rgba(185, 234, 69, 0)',
    duration: 0.4,
    ease: 'power2.inOut',
  }, 0.3)
}

function handleWPUpdate(event: CustomEvent) {
  const newValue = event.detail.whalePoints
  animatePill(newValue)
}

// Watch for prop changes and animate
watch(() => props.whalePoints, (newValue, oldValue) => {
  if (newValue !== oldValue && newValue !== displayValue.value) {
    animatePill(newValue)
  }
})

onMounted(() => {
  if (!props.fixed) {
    window.addEventListener('wp-update', handleWPUpdate as EventListener)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('wp-update', handleWPUpdate as EventListener)
})
</script>

<style scoped lang="scss">
.wp-pill {
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(185, 234, 69, 0.12);
  border: 1px solid rgba(185, 234, 69, 0.35);
  color: $accent-lime-2;
  font-weight: 700;
  font-size: 12px;
  cursor: default;

}
</style>